import { create } from 'zustand'
import axiosInstance from 'libs/shared/data-access/src/lib/clients/axiosClient/axiosConfig'
import { getMockNoDigitalSlots } from '../components/no-digital-calendar/mock-data/mock-data-helper'
import { setLocalStorageItem } from '@shared/utils'

const USE_MOCK_DATA = false

const BYPASS_ASSIGN_SLOTS = false

const IS_TEST_ENV =
  window.location.hostname.includes('test') ||
  window.location.hostname.includes('localhost')

const detectCurrentApp = () => {
  const businessAgentAPI = process.env?.['NX_BUSINESS_AGENT_API_URL'] || ''
  const socialReferentAPI = process.env?.['NX_SOCIAL_REFERENT_API_URL'] || ''
  const currentHostname = window.location.hostname
  const currentPort = window.location.port

  const isLocalBusinessAgent = currentPort === '4200'
  const isLocalSocialReferent = currentPort === '4202'

  const isBusinessAgent =
    isLocalBusinessAgent ||
    (!isLocalSocialReferent && (
      (businessAgentAPI &&
        (businessAgentAPI.includes(currentHostname) ||
          currentHostname.includes('ba-') ||
          currentHostname.includes('business-agent'))) ||
      currentHostname.includes('ba-portal')
    ))

  const isSocialReferent =
    isLocalSocialReferent ||
    (!isLocalBusinessAgent && (
      (socialReferentAPI &&
        (socialReferentAPI.includes(currentHostname) ||
          currentHostname.includes('sr-') ||
          currentHostname.includes('social-referent'))) ||
      currentHostname.includes('sr-portal')
    ))

  let baseURL = ''
  if (isBusinessAgent) {
    baseURL = businessAgentAPI
  } else if (isSocialReferent) {
    baseURL = socialReferentAPI
  } else {
    baseURL = businessAgentAPI
  }

  return { isBusinessAgent, isSocialReferent, baseURL }
}

export interface NoDigitalSlot {
  id: string
  start: string
  end: string
  effective_start: string
  effective_end: string
  type: string
  available_social_referents: string[]
}

export interface NoDigitalSlotDate {
  startDate: string
  endDate: string
}
export enum SlotType {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
  PRIORITY = 'PRIORITY',
}
export interface SelectedSlotData {
  start: string
  end: string
  effective_start: string
  effective_end: string
  available_social_referents: string[]
  type: SlotType;
}

interface NoDigitalCalendarStore {
  slots: NoDigitalSlot[]
  loading: boolean
  error: string | null
  errorType: 'slots' | 'assign_slots' | 'existing_appointment' | null
  selectedSlot: NoDigitalSlotDate | null
  selectedSlotData: SelectedSlotData | null
  assigningSlot: boolean
  isUsingMockData: boolean

  fetchSlots: (policyId: string) => Promise<void>
  setSelectedSlot: (
    slot: NoDigitalSlotDate | null,
    slotData?: SelectedSlotData | null,
  ) => void
  assignSlot: (policyId: string) => Promise<boolean>
  patchPolicyNoDigital: (policyId: string) => Promise<boolean>
  deleteAppointment: (policyId: string) => Promise<boolean>
  clearSlots: () => void
  clearAllNoDigitalData: () => void
  ensureCsrfToken: () => Promise<void>
}

export const useNoDigitalCalendarStore = create<NoDigitalCalendarStore>(
  (set, get) => ({
    slots: [],
    loading: false,
    error: null,
    errorType: null,
    selectedSlot: null,
    selectedSlotData: null,
    assigningSlot: false,
    isUsingMockData: false,

    fetchSlots: async (policyId: string) => {
      set({ loading: true, error: null, errorType: null })

      try {
        let slotsArray: any[] = []

        if (USE_MOCK_DATA) {
          const mockResponse = getMockNoDigitalSlots()
          slotsArray = mockResponse.data
          set({ isUsingMockData: true })
        } else {
          await get().ensureCsrfToken()

          const { baseURL } = detectCurrentApp()
          const url = `${baseURL}policies/${policyId}/slots/`

          const response = await axiosInstance.get(url)

          if (Array.isArray(response.data)) {
            slotsArray = response.data
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            slotsArray = response.data.data
          } else if (response.data && typeof response.data === 'object') {
            slotsArray = [response.data]
          }
        }

        const transformedSlots = slotsArray.map((slot: any, index: number) => ({
          id: slot.id || `slot-${index}`,
          start: slot.attributes?.start || slot.start || slot.effective_start,
          end: slot.attributes?.end || slot.end || slot.effective_end,
          effective_start:
            slot.attributes?.effective_start ||
            slot.effective_start ||
            slot.start,
          effective_end:
            slot.attributes?.effective_end || slot.effective_end || slot.end,
          type: slot.attributes?.type || slot.type || 'AVAILABLE',
          available_social_referents:
            slot.attributes?.available_social_referents ||
            slot.available_social_referents ||
            [],
        }))

        set({ slots: transformedSlots, loading: false, isUsingMockData: false })
      } catch (error) {
        console.error('❌ Error fetching non-digital slots:', error)

        const axiosError = error as any
        if (axiosError.response) {
          console.error('❌ Response status:', axiosError.response.status)
          console.error('❌ Response data:', axiosError.response.data)
          console.error('❌ Response headers:', axiosError.response.headers)

          if (axiosError.response.status === 403) {
            localStorage.removeItem('csrftoken')

            try {
              await get().ensureCsrfToken()

              const { baseURL } = detectCurrentApp()

              const url = `${baseURL}policies/${policyId}/slots/`
              const retryResponse = await axiosInstance.get(url)

              let retrySlotsArray: any[] = []
              if (Array.isArray(retryResponse.data)) {
                retrySlotsArray = retryResponse.data
              } else if (
                retryResponse.data?.data &&
                Array.isArray(retryResponse.data.data)
              ) {
                retrySlotsArray = retryResponse.data.data
              } else if (
                retryResponse.data &&
                typeof retryResponse.data === 'object'
              ) {
                retrySlotsArray = [retryResponse.data]
              }

              const retryTransformedSlots = retrySlotsArray.map(
                (slot: any, index: number) => ({
                  id: slot.id || `slot-${index}`,
                  start:
                    slot.attributes?.start ||
                    slot.start ||
                    slot.effective_start,
                  end: slot.attributes?.end || slot.end || slot.effective_end,
                  effective_start:
                    slot.attributes?.effective_start ||
                    slot.effective_start ||
                    slot.start,
                  effective_end:
                    slot.attributes?.effective_end ||
                    slot.effective_end ||
                    slot.end,
                  type: slot.attributes?.type || slot.type || 'AVAILABLE',
                  available_social_referents:
                    slot.attributes?.available_social_referents ||
                    slot.available_social_referents ||
                    [],
                }),
              )

              set({
                slots: retryTransformedSlots,
                loading: false,
                isUsingMockData: false,
              })
              return
            } catch (retryError) {
              console.error('❌ Retry also failed:', retryError)

              if (IS_TEST_ENV && !USE_MOCK_DATA) {
                const mockResponse = getMockNoDigitalSlots()
                const mockSlots = mockResponse.data.map(
                  (slot: any, index: number) => ({
                    id: slot.id || `slot-${index}`,
                    start:
                      slot.attributes?.start ||
                      slot.start ||
                      slot.effective_start,
                    end: slot.attributes?.end || slot.end || slot.effective_end,
                    effective_start:
                      slot.attributes?.effective_start ||
                      slot.effective_start ||
                      slot.start,
                    effective_end:
                      slot.attributes?.effective_end ||
                      slot.effective_end ||
                      slot.end,
                    type: slot.attributes?.type || slot.type || 'AVAILABLE',
                    available_social_referents:
                      slot.attributes?.available_social_referents ||
                      slot.available_social_referents ||
                      [],
                  }),
                )

                set({
                  slots: mockSlots,
                  loading: false,
                  error: null,
                  isUsingMockData: true,
                })
                return
              }
            }
          }
        }

        set({
          error:
            error instanceof Error ? error.message : 'Failed to fetch slots',
          errorType: 'slots',
          loading: false,
          slots: [],
        })
      }
    },

    setSelectedSlot: (
      slot: NoDigitalSlotDate | null,
      slotData?: SelectedSlotData | null,
    ) => {
      set({ selectedSlot: slot, selectedSlotData: slotData || null })

      if (slot) {
        setLocalStorageItem('dateSelected', slot.startDate)

        const extractTimeFromISO = (isoString: string): string => {
          const date = new Date(isoString)
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          return `${hours}:${minutes}`
        }

        // Let JavaScript handle timezone conversion automatically
        const startTime = extractTimeFromISO(slot.startDate)
        const endTime = extractTimeFromISO(slot.endDate)
        const timeFormatted = `${startTime} - ${endTime}`

        setLocalStorageItem('slotDelected', timeFormatted)
        setLocalStorageItem('slotsSelected', timeFormatted)
      }
    },

    patchPolicyNoDigital: async (policyId: string) => {
      try {
        const { baseURL } = detectCurrentApp()
        const url = `${baseURL}policies/${policyId}/`

        const policyData: any = {
          no_digital: true,
        }

        const officeCode = localStorage.getItem('noDigitalOfficeCode')
        const officeName = localStorage.getItem('noDigitalOfficeName')
        const officeAddress = localStorage.getItem('noDigitalOfficeAddress')

        const isOfficeAppointment = !!(officeCode || officeName)

        if (isOfficeAppointment) {
          policyData.office_control = true
          policyData.manager_office_direction = officeAddress || null
        } else {
          policyData.office_control = false
          policyData.manager_office_direction = null
        }

        console.log(
          `🏢 PATCH Policy no-digital data (${isOfficeAppointment ? 'OFICINA' : 'DOMICILIO'}):`,
          {
            policyId,
            url,
            payload: policyData,
            appointmentType: isOfficeAppointment
              ? 'Presencial en oficina'
              : 'Presencial en domicilio',
          },
        )

        await axiosInstance.patch(url, policyData)

        console.log('✅ Policy no-digital data patched successfully')
        return true
      } catch (error) {
        console.error('❌ Failed to patch policy no-digital data:', error)
        throw error
      }
    },

    assignSlot: async (policyId: string) => {
      const { selectedSlotData } = get()

      if (!selectedSlotData) {
        return false
      }

      set({ assigningSlot: true, error: null, errorType: null })

      try {
        if (BYPASS_ASSIGN_SLOTS) {
          console.log('🚫 BYPASSING assign_slots API call for testing')
          console.log('📝 Selected slot data:', selectedSlotData)

          await new Promise((resolve) => setTimeout(resolve, 1000))

          await get().patchPolicyNoDigital(policyId)

          set({ assigningSlot: false })
          return true
        }

        await get().ensureCsrfToken()

        const { baseURL } = detectCurrentApp()

        const url = `${baseURL}policies/${policyId}/assign_slots/`

        const { isUsingMockData } = get()

        const { isSocialReferent } = detectCurrentApp()

        const email = localStorage.getItem('noDigitalEmail')
        const phone = localStorage.getItem('noDigitalPhone')
        const officeCode = localStorage.getItem('noDigitalOfficeCode')
        const officeName = localStorage.getItem('noDigitalOfficeName')
        const officeAddress = localStorage.getItem('noDigitalOfficeAddress')

        let locationType = 'ONLINE'
        let location: string | null = 'Videollamada'

        if (officeCode || officeName) {
          locationType = 'OFFICE'
          location = officeAddress || officeName || 'Oficina seleccionada'
        } else if (email && phone) {
          locationType = 'HOME'
          location = null
        } else if (isSocialReferent) {

          locationType = 'HOME'
          location = null
        }

        const assignSlotsPayload: any = {
          slot: {
            start: selectedSlotData.start,
            end: selectedSlotData.end,
            effective_start: selectedSlotData.effective_start,
            effective_end: selectedSlotData.effective_end,
            type: selectedSlotData.type,
            available_social_referents: isUsingMockData
              ? []
              : selectedSlotData.available_social_referents,
          },
          location_type: locationType,
        }

        if (!isSocialReferent) {
          assignSlotsPayload.email = email || ''
          assignSlotsPayload.phone = phone || ''
        }

        if (locationType !== 'HOME') {
          assignSlotsPayload.location = location
        }

        console.log('📤 Sending assign_slots payload:', assignSlotsPayload)

        await axiosInstance.post(url, assignSlotsPayload)

        await new Promise((resolve) => setTimeout(resolve, 1000))

        set({ assigningSlot: false })
        return true
      } catch (error) {
        set({
          error:
            error instanceof Error ? error.message : 'Failed to assign slot',
          errorType: 'assign_slots',
          assigningSlot: false,
        })
        return false
      }
    },

    clearSlots: () => {
      set({
        slots: [],
        selectedSlot: null,
        selectedSlotData: null,
        error: null,
        errorType: null,
        assigningSlot: false,
        isUsingMockData: false,
      })
      localStorage.removeItem('dateSelected')
      localStorage.removeItem('slotDelected')
      localStorage.removeItem('slotsSelected')
    },

    clearAllNoDigitalData: () => {
      console.log('🧹 Clearing all no-digital data from Zustand store')

      set({
        slots: [],
        selectedSlot: null,
        selectedSlotData: null,
        error: null,
        errorType: null,
        assigningSlot: false,
        isUsingMockData: false,
      })

      const keysToClean = [
        'dateSelected',
        'slotDelected',
        'slotsSelected',
        'noDigitalEmail',
        'noDigitalPhone',
        'noDigitalOfficeCode',
        'noDigitalOfficeName',
        'noDigitalOfficeAddress',
        'noDigitalOfficeId',
        'clicked',
        'no_digital',
        'previousDateSelected',
        'blockingAppointmentCheck',
      ]

      keysToClean.forEach((key) => localStorage.removeItem(key))
    },

    deleteAppointment: async (policyId: string) => {
      try {
        console.log('🗑️ Deleting existing appointment for policy:', policyId)

        await get().ensureCsrfToken()

        const { baseURL } = detectCurrentApp()
        const url = `${baseURL}delete_appointments/`

        const deleteParams = {
          policy: policyId,
        }

        console.log('🗑️ DELETE appointment request:', {
          url,
          params: deleteParams
        })

        await axiosInstance.delete(url, {
          params: deleteParams
        })

        console.log('✅ Appointment deleted successfully for policy:', policyId)
        return true
      } catch (error) {
        console.error('❌ Failed to delete appointment:', error)
        return false
      }
    },

    ensureCsrfToken: async () => {
      const existingToken = localStorage.getItem('csrftoken')

      if (!existingToken) {
        try {
          const { baseURL } = detectCurrentApp()

          const authUrl = `${baseURL}auth`

          const response = await axiosInstance.get(authUrl)

          if (response?.data?.data?.attributes?.csrfToken) {
            localStorage.setItem(
              'csrftoken',
              response.data.data.attributes.csrfToken,
            )
          } else {
            console.warn('⚠️ No CSRF token in auth response:', response?.data)
          }
        } catch (error) {
          console.error('❌ Failed to get CSRF token from /auth:', error)
          throw new Error('Unable to obtain authentication token')
        }
      } else {
        console.warn('✅ CSRF token already exists in localStorage')
      }
    },
  }),
)
